import React from "react";
import { Settings2 } from "lucide-react";
import SearchIcon from "../../assets/icons/search-icon";
import DatePickerInput from "../forms/date-picker";
import { DUE_FILTERS } from "../../utils/follow-up";

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "done", label: "Completed" },
  { key: "all", label: "All" },
];

const tabCount = (summary, key) => {
  if (!summary) return null;
  if (key === "all") return (summary.pending ?? 0) + (summary.done ?? 0);
  return summary[key] ?? 0;
};

const TEXT_CLASS =
  "text-[length:var(--head-input-font-size)] font-[var(--head-input-font-weight)]";

// A segment of the status / refill date toggles: selected ones take the list
// table's heading colour
const segmentClass = (isActive) =>
  `flex h-full items-center gap-1.5 px-3 whitespace-nowrap ${TEXT_CLASS} transition-colors focus-visible:outline-none ${
    isActive
      ? "bg-[var(--list-table-heading-bg)] text-[var(--list-table-heading-fg-color)]"
      : "bg-[var(--head-input-bg-color)] text-[var(--head-input-fg-color)] hover:bg-[var(--list-table-row-hover-bg)]"
  }`;

const SEGMENTS_CLASS =
  "flex h-[32px] divide-x divide-[var(--list-table-border-color)] overflow-hidden border border-[var(--list-table-border-color)] border-radius";

// Same layout as the other list pages' filter row (filtering-section2)
const FollowUpToolbar = ({
  filters,
  onFiltersChange,
  searchInput,
  onSearchChange,
  summary,
  onOpenMessageSettings,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-[24px] py-[16px]">
    <label
      style={{ maxWidth: "30rem", height: "32px" }}
      className="flex w-full items-center border border-[var(--head-input-border-color)] bg-[var(--head-input-bg-color)] border-radius focus-within:border-[var(--head-input-focus-border-color)] sm:w-[22rem]"
    >
      <span className="sr-only">Search follow-ups</span>
      <input
        type="text"
        maxLength={50}
        value={searchInput}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search customer, phone, item or bill no."
        className={`h-full w-full rounded-md bg-[var(--head-input-bg-color)] px-[8px] text-[var(--head-input-fg-color)] ${TEXT_CLASS} placeholder:text-[var(--head-input-placeholder-color)] placeholder:font-[var(--head-input-place-font-weight)] focus_outline__none`}
      />
      <span className="pr-2">
        <SearchIcon />
      </span>
    </label>

    <div className="flex flex-wrap items-center gap-3">
      <div role="tablist" aria-label="Follow-up status" className={SEGMENTS_CLASS}>
        {STATUS_TABS.map((tab) => {
          const isActive = filters.status === tab.key;
          const count = tabCount(summary, tab.key);
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onFiltersChange({ status: tab.key })}
              className={segmentClass(isActive)}
            >
              {tab.label}
              {count !== null && <span className="opacity-80">({count})</span>}
            </button>
          );
        })}
      </div>

      <div role="group" aria-label="Refill date" className={SEGMENTS_CLASS}>
        {DUE_FILTERS.map((due) => (
          <button
            key={due.key}
            type="button"
            aria-pressed={filters.due === due.key}
            onClick={() => onFiltersChange({ due: due.key })}
            className={segmentClass(filters.due === due.key)}
          >
            {due.label}
          </button>
        ))}
      </div>

      {filters.due === "custom" && (
        <div className="flex items-center gap-2">
          <DatePickerInput
            placeholder="From Date"
            value={filters.fromDate}
            maxDate={filters.toDate || null}
            onChange={(date) => onFiltersChange({ fromDate: date })}
          />
          <DatePickerInput
            placeholder="To Date"
            value={filters.toDate}
            minDate={filters.fromDate || null}
            onChange={(date) => onFiltersChange({ toDate: date })}
          />
        </div>
      )}

      <button
        type="button"
        onClick={onOpenMessageSettings}
        title="Edit the WhatsApp follow-up message"
        className={`flex h-[32px] items-center gap-2 px-3 whitespace-nowrap border border-[var(--list-table-border-color)] bg-[var(--head-input-bg-color)] text-[var(--head-input-fg-color)] ${TEXT_CLASS} border-radius transition-colors hover:bg-[var(--list-table-row-hover-bg)]`}
      >
        <Settings2 size={14} aria-hidden="true" />
        WhatsApp Message
      </button>
    </div>
  </div>
);

export default FollowUpToolbar;
